import { useMemo } from 'react';
import { mainMenuItems, orgMigrationMenuItem } from './utils/utils';
import { appRoutes, isRoutePathEnabledForOrgType } from '../../../constants/appRoutes';
import type { MainMenuItem, MenuItem, MenuColumn } from './utils/types';
import { useSelector } from 'react-redux';
import { selectIsDisableAi } from '../../../reducers/userInfoReducer';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import type { OrgType } from '../../../apis/facades/types/crmOrgsApiFacade.types';
import { useDefaultEnv } from '../../pages/environments/default-env-context/DefaultEnvContext';

const applyOrgTypeDisabledState = (menuItem: MenuItem, orgType: OrgType | undefined): MenuItem => {
  const isDisabled = !isRoutePathEnabledForOrgType(menuItem.route, orgType);

  const subMenuItems = menuItem.subMenuItems?.map((sub) => applyOrgTypeDisabledState(sub, orgType));

  const allSubItemsDisabled =
    subMenuItems && subMenuItems.length > 0 && subMenuItems.every((sub) => sub.isDisabled);

  return {
    ...menuItem,
    isDisabled: isDisabled || allSubItemsDisabled,
    ...(subMenuItems && { subMenuItems }),
  };
};

const applyOrgTypeToColumns = (
  columns: MenuColumn[],
  orgType: OrgType | undefined,
): { columns: MenuColumn[]; allDisabled: boolean } => {
  const updatedColumns = columns.map((col) => ({
    ...col,
    menuItems: col.menuItems.map((mi) => applyOrgTypeDisabledState(mi, orgType)),
  }));

  const allDisabled = updatedColumns.every((col) => col.menuItems.every((mi) => mi.isDisabled));

  return { columns: updatedColumns, allDisabled };
};

export const useMenuItemsWithPermissions = (): MainMenuItem[] => {
  const isDisableAi = useSelector(selectIsDisableAi);
  const defaultCreationEnv = useDefaultEnv();
  const orgType = defaultCreationEnv?.orgType;

  const { orgMigrationDemo, projectV1 } = useFeatureToggle();

  const menuItemsWithPermissions = useMemo(() => {
    return mainMenuItems.map((item) => {
      if (item.route === appRoutes.agenticLayer.route) {
        return {
          ...item,
          menuColumns: projectV1 ? item.menuColumns : undefined,
          isHidden: isDisableAi,
        };
      }

      let currentItem = item;

      if (orgMigrationDemo && currentItem.route === 'apps1' && currentItem.menuColumns) {
        currentItem = {
          ...currentItem,
          menuColumns: [
            {
              ...currentItem.menuColumns[0],
              menuItems: [...currentItem.menuColumns[0].menuItems, orgMigrationMenuItem],
            },
            ...currentItem.menuColumns.slice(1),
          ],
        };
      }

      if (currentItem.menuColumns) {
        const { columns, allDisabled } = applyOrgTypeToColumns(currentItem.menuColumns, orgType);
        return { ...currentItem, menuColumns: columns, isDisabled: allDisabled };
      }

      const isDisabled = !isRoutePathEnabledForOrgType(currentItem.route, orgType);
      return { ...currentItem, isDisabled };
    });
  }, [isDisableAi, orgType, orgMigrationDemo, projectV1]);

  return menuItemsWithPermissions;
};
